import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Tag,
  Tree,
  DatePicker,
  Row,
  Col,
  message,
  Tabs,
  Statistic,
} from 'antd';
import {
  ReloadOutlined,
  DollarOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import AuthGuard from '../components/AuthGuard';
import {
  getReferralTree,
  getReferralEarnings,
} from '../services/adminApi';
import type { User } from '../types';
import dayjs from 'dayjs';

const { Search } = Input;
const { RangePicker } = DatePicker;

const roleMap: Record<string, string> = {
  admin: '管理员',
  level1: '一级推荐官',
  level2: '二级推荐官',
  level3: '三级推荐官',
  retail: '用电用户'
};

const roleColorMap: Record<string, string> = {
  admin: 'red',
  level1: 'green',
  level2: 'orange',
  level3: 'purple',
  retail: 'blue'
};

interface ReferralTreeNode {
  id: number;
  phone: string;
  nickname: string;
  role: string;
  status: string;
  referralCode: string;
  createdAt: string;
  children?: ReferralTreeNode[];
  profile?: {
    companyName?: string;
    realName?: string;
  };
}

interface EarningsRecord {
  id: number;
  userId: number;
  type: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  baseAmount?: string;
  rate?: string;
  profitAmount: string;
  bonusAmount: string;
  totalAmount: string;
  status: string;
  settledAt?: string;
  createdAt: string;
  user: {
    phone: string;
    nickname: string;
    role: string;
    profile?: {
      companyName?: string;
      realName?: string;
    };
  };
}

const ReferralManagement: React.FC = () => {
  const [treeData, setTreeData] = useState<ReferralTreeNode[]>([]);
  const [earnings, setEarnings] = useState<EarningsRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [userIdFilter, setUserIdFilter] = useState<number | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [earningsPagination, setEarningsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 获取推荐官层级树
  const fetchReferralTree = async () => {
    setLoading(true);
    try {
      const response = await getReferralTree();
      if (response.data.success) {
        setTreeData(response.data.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取推荐官层级关系失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取推荐官收益
  const fetchEarnings = async (page = 1, pageSize = 10) => {
    setEarningsLoading(true);
    try {
      const response = await getReferralEarnings({
        page,
        pageSize,
        userId: userIdFilter,
        periodStart: dateRange?.[0]?.format('YYYY-MM-DD'),
        periodEnd: dateRange?.[1]?.format('YYYY-MM-DD'),
      });

      if (response.data.success) {
        setEarnings(response.data.data.list);
        setEarningsPagination({
          current: response.data.data.page,
          pageSize: response.data.data.pageSize,
          total: response.data.data.total,
        });
      }
    } catch (error: any) {
      message.error(error.message || '获取推荐官收益失败');
    } finally {
      setEarningsLoading(false);
    }
  };

  useEffect(() => {
    fetchReferralTree();
    fetchEarnings();
  }, [userIdFilter, dateRange]);

  // 递归转换树形数据
  const formatTreeNode = (node: ReferralTreeNode): any => {
    return {
      title: (
        <div>
          <div>
            <strong>{node.nickname || '未设置'}</strong>
            <Tag color={roleColorMap[node.role] || 'blue'} style={{ marginLeft: 8 }}>
              {roleMap[node.role] || node.role}
            </Tag>
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {node.phone} | {node.referralCode}
          </div>
        </div>
      ),
      key: node.id,
      children: node.children && node.children.length > 0
        ? node.children.map(child => formatTreeNode(child))
        : undefined,
    };
  };

  const treeDataFormatted = treeData.map(node => formatTreeNode(node));

  const earningsColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户',
      dataIndex: ['user', 'nickname'],
      key: 'user',
      render: (nickname: string, record: EarningsRecord) => (
        <div>
          <div>{nickname || '未设置'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.user.phone}
          </div>
        </div>
      ),
    },
    {
      title: '角色',
      dataIndex: ['user', 'role'],
      key: 'role',
      render: (role: string) => {
        return <Tag color={roleColorMap[role] || 'blue'}>{roleMap[role] || role}</Tag>;
      },
    },
    {
      title: '分润类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const typeMap: Record<string, { label: string; color: string }> = {
          basic: { label: '基础分润', color: 'blue' },
          management: { label: '管理分润', color: 'orange' },
          team: { label: '团队分润', color: 'purple' },
        };
        const typeInfo = typeMap[type] || typeMap.basic;
        return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>;
      },
    },
    {
      title: '周期',
      key: 'period',
      render: (_: any, record: EarningsRecord) => (
        <div>
          <div>{record.periodStart}</div>
          <div>至</div>
          <div>{record.periodEnd}</div>
        </div>
      ),
    },
    {
      title: '基础金额',
      dataIndex: 'baseAmount',
      key: 'baseAmount',
      render: (amount: string) => amount ? `¥${parseFloat(amount).toLocaleString()}` : '-',
    },
    {
      title: '分润比例',
      dataIndex: 'rate',
      key: 'rate',
      render: (rate: string) => rate ? `${(parseFloat(rate) * 100).toFixed(2)}%` : '-',
    },
    {
      title: '分润金额',
      dataIndex: 'profitAmount',
      key: 'profitAmount',
      render: (amount: string) => `¥${parseFloat(amount).toLocaleString()}`,
    },
    {
      title: '奖励金额',
      dataIndex: 'bonusAmount',
      key: 'bonusAmount',
      render: (amount: string) => amount ? `¥${parseFloat(amount).toLocaleString()}` : '-',
    },
    {
      title: '总金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: string) => `¥${parseFloat(amount).toLocaleString()}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          pending: { label: '待结算', color: 'warning' },
          confirmed: { label: '已确认', color: 'processing' },
          paid: { label: '已支付', color: 'success' },
        };
        const statusInfo = statusMap[status] || statusMap.pending;
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
      },
    },
    {
      title: '结算时间',
      dataIndex: 'settledAt',
      key: 'settledAt',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
  ];

  const handleEarningsTableChange = (pagination: any) => {
    fetchEarnings(pagination.current, pagination.pageSize);
  };

  // 计算总收益
  const totalEarnings = earnings.reduce((sum, record) => {
    return sum + parseFloat(record.totalAmount);
  }, 0);

  const tabItems = [
    {
      key: 'tree',
      label: (
        <span>
          <TeamOutlined />
          推荐官层级
        </span>
      ),
      children: (
        <Card
          title="推荐官层级关系"
          extra={
            <Button icon={<ReloadOutlined />} onClick={fetchReferralTree}>
              刷新
            </Button>
          }
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>
          ) : treeData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              暂无推荐官数据
            </div>
          ) : (
            <Tree
              showLine
              treeData={treeDataFormatted}
              defaultExpandAll
            />
          )}
        </Card>
      ),
    },
    {
      key: 'earnings',
      label: (
        <span>
          <DollarOutlined />
          推荐官收益
        </span>
      ),
      children: (
        <Card
          title="推荐官收益情况"
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => fetchEarnings()}>
              刷新
            </Button>
          }
        >
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col span={24}>
              <Statistic
                title="总收益"
                value={totalEarnings}
                prefix="¥"
                precision={2}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
          </Row>

          <div style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={6}>
                <Search
                  placeholder="搜索用户ID"
                  onSearch={(value) => setUserIdFilter(value ? parseInt(value) : undefined)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <RangePicker
                  placeholder={['开始日期', '结束日期']}
                  onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
          </div>

          <Table
            columns={earningsColumns}
            dataSource={earnings}
            rowKey="id"
            loading={earningsLoading}
            pagination={{
              ...earningsPagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
            onChange={handleEarningsTableChange}
            scroll={{ x: 1500 }}
          />
        </Card>
      ),
    },
  ];

  return (
    <AuthGuard>
      <div>
        <Tabs defaultActiveKey="tree" items={tabItems} />
      </div>
    </AuthGuard>
  );
};

export default ReferralManagement;